import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'
import { defineStore } from 'pinia'

import {
  bundleHasSheetSelectionGroup,
  getAutoSelectedRequiredItems,
  getSheetSelectionGroup
} from '@/lib/packageBuilder'
import { mapCartBundleToCartBundleUpsert } from '@/lib/utils'
import { appErrorService } from '@/services/AppErrorService'

import type { CatalogBundleStorefrontDtoWithContent } from '@/types/CatalogBundleStorefrontDtoWithContent'

import type {
  CartBundleStorefrontDto,
  CartItemCustomizationDetailsDto,
  ItemGroupStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import { CartItemUpsertStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

interface PackageBuilderState {
  // The editor modal's open flag. The dedicated step configures bundles inline
  // (no flag); the modal is a dormant per-bundle editor for a future
  // edit-after-add entry point.
  isOpen: boolean
  // The cart bundle the editor modal is editing (null when closed).
  editCartBundleId: string | null
  // Per-cart-bundle sheet selections, keyed by `cartBundleId`. Shared by the
  // step's panels and the editor modal.
  selectionsByCartBundle: Record<string, Record<string, number>>
  // Cart bundles the customer has *deliberately* configured (built + submitted on
  // the step, or edited via the modal). Persisted. Distinguishes "added" from
  // "configured": a freshly-added builder bundle carries only a backend default
  // seed (see `getDefaultSheetSelectionItems`), which the UI hides — the step shows
  // an empty panel until the bundle is configured. The cart can't carry this marker
  // (`CartBundleUpsertStorefrontDto` has no `attributes`), so it lives client-side.
  configuredCartBundleIds: string[]
  isSubmitting: boolean
}

export const usePackageBuilderStore = defineStore('packageBuilder', {
  state: (): PackageBuilderState => ({
    isOpen: false,
    editCartBundleId: null,
    selectionsByCartBundle: {},
    configuredCartBundleIds: [],
    isSubmitting: false
  }),
  getters: {
    /** Cart bundles whose catalog bundle carries a SheetSelection item group. */
    packageBuilderCartBundles(): CartBundleStorefrontDto[] {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()
      return cartStore.cartBundles.filter((cb) => {
        const catalogBundle = catalogStore.catalogBundlesWithContent.find(
          (cbwc) => cbwc.catalogBundleId === cb.catalogBundleId
        )
        return bundleHasSheetSelectionGroup(catalogBundle?.bundle)
      })
    },
    catalogBundleForCartBundle(): (
      cartBundleId: string
    ) => CatalogBundleStorefrontDtoWithContent | null {
      const cartStore = useCartStore()
      const catalogStore = useCatalogStore()
      return (cartBundleId: string) => {
        const cartBundle = cartStore.cartBundles.find((cb) => cb.cartBundleId === cartBundleId)
        if (!cartBundle) return null
        return (
          catalogStore.catalogBundlesWithContent.find(
            (cbwc) => cbwc.catalogBundleId === cartBundle.catalogBundleId
          ) ?? null
        )
      }
    },
    /** The bundle's content name, else its internal name; null when it has neither, or both are empty. */
    bundleNameForCartBundle(): (cartBundleId: string) => string | null {
      return (cartBundleId: string) => {
        const catalogBundle = this.catalogBundleForCartBundle(cartBundleId)
        return catalogBundle?.content?.name || catalogBundle?.bundle?.nameInternal || null
      }
    },
    sheetSelectionGroupForCartBundle(): (cartBundleId: string) => ItemGroupStorefrontDto | null {
      return (cartBundleId: string) =>
        getSheetSelectionGroup(this.catalogBundleForCartBundle(cartBundleId)?.bundle)
    },
    targetCountForCartBundle(): (cartBundleId: string) => number {
      return (cartBundleId: string) =>
        this.sheetSelectionGroupForCartBundle(cartBundleId)?.maxAllowedItems ?? 0
    },
    totalSelectedForCartBundle(state): (cartBundleId: string) => number {
      return (cartBundleId: string) =>
        Object.values(state.selectionsByCartBundle[cartBundleId] ?? {}).reduce(
          (sum, q) => sum + q,
          0
        )
    },
    remainingForCartBundle(): (cartBundleId: string) => number {
      return (cartBundleId: string) =>
        Math.max(
          0,
          this.targetCountForCartBundle(cartBundleId) -
            this.totalSelectedForCartBundle(cartBundleId)
        )
    },
    isValidForCartBundle(): (cartBundleId: string) => boolean {
      return (cartBundleId: string) => {
        const target = this.targetCountForCartBundle(cartBundleId)
        return target > 0 && this.totalSelectedForCartBundle(cartBundleId) === target
      }
    },
    /** True when every SheetSelection bundle in the cart is at its exact target. */
    allValid(): boolean {
      return this.packageBuilderCartBundles.every((cb) =>
        this.isValidForCartBundle(cb.cartBundleId)
      )
    },
    /**
     * True when a SheetSelection bundle is in the cart but not yet *deliberately*
     * configured (added with only the backend default seed). Drives the router
     * backstop that blocks navigation past the Package Builder step until the
     * customer finishes it.
     */
    hasUnconfiguredBundles(): boolean {
      return this.packageBuilderCartBundles.some(
        (cb) => !this.configuredCartBundleIds.includes(cb.cartBundleId)
      )
    },
    /**
     * The sheets a cart bundle holds in the cart, per sheet-selection item group item.
     * `quantity` is summed, so the per-sheet unroll maps back to a count.
     */
    cartSheetCountsForCartBundle(): (cartBundleId: string) => Record<string, number> {
      return (cartBundleId: string) => {
        const cartBundle = this.packageBuilderCartBundles.find(
          (cb) => cb.cartBundleId === cartBundleId
        )
        const group = this.sheetSelectionGroupForCartBundle(cartBundleId)
        const groupItemIds = new Set(
          (group?.itemGroupItems ?? []).map((igi) => igi.itemGroupItemId)
        )

        const counts: Record<string, number> = {}
        for (const cartItem of cartBundle?.cartItems ?? []) {
          const id = cartItem.itemGroupItemId
          if (!id || !groupItemIds.has(id)) continue
          counts[id] = (counts[id] ?? 0) + (cartItem.quantity || 1)
        }
        return counts
      }
    },
    /** Whether the working sheet selection differs from what the cart holds. */
    sheetsChangedForCartBundle(state): (cartBundleId: string) => boolean {
      return (cartBundleId: string) => {
        const inCart = this.cartSheetCountsForCartBundle(cartBundleId)
        const working = state.selectionsByCartBundle[cartBundleId] ?? {}
        const ids = new Set([...Object.keys(inCart), ...Object.keys(working)])
        return [...ids].some((id) => (inCart[id] ?? 0) !== (working[id] ?? 0))
      }
    }
  },
  actions: {
    /**
     * Rebuilds `selectionsByCartBundle` from the cart so the UI reflects
     * whatever the add-time `CartBundleConfigurator` seeded for each
     * SheetSelection bundle (empty or a partial default). Only items belonging
     * to a bundle's SheetSelection group are counted; `quantity` is summed so
     * the per-sheet unroll (one cart item per sheet) maps back to a count.
     */
    hydrateFromCart() {
      const next: Record<string, Record<string, number>> = {}

      for (const cartBundle of this.packageBuilderCartBundles) {
        // Unconfigured bundles start empty so the customer builds deliberately. Their
        // cart holds only the backend default seed (see `getDefaultSheetSelectionItems`),
        // which we intentionally don't surface until the bundle has been configured.
        if (!this.configuredCartBundleIds.includes(cartBundle.cartBundleId)) {
          next[cartBundle.cartBundleId] = {}
          continue
        }

        next[cartBundle.cartBundleId] = this.cartSheetCountsForCartBundle(cartBundle.cartBundleId)
      }

      this.selectionsByCartBundle = next
    },
    incrementSheetForCartBundle(cartBundleId: string, itemGroupItemId: string) {
      if (
        this.totalSelectedForCartBundle(cartBundleId) >= this.targetCountForCartBundle(cartBundleId)
      ) {
        return
      }
      const bundleSelections = this.selectionsByCartBundle[cartBundleId] ?? {}
      const current = bundleSelections[itemGroupItemId] ?? 0
      this.selectionsByCartBundle = {
        ...this.selectionsByCartBundle,
        [cartBundleId]: { ...bundleSelections, [itemGroupItemId]: current + 1 }
      }
    },
    decrementSheetForCartBundle(cartBundleId: string, itemGroupItemId: string) {
      const bundleSelections = this.selectionsByCartBundle[cartBundleId] ?? {}
      const current = bundleSelections[itemGroupItemId] ?? 0
      if (current <= 0) return
      const nextBundle = { ...bundleSelections }
      if (current - 1 === 0) {
        delete nextBundle[itemGroupItemId]
      } else {
        nextBundle[itemGroupItemId] = current - 1
      }
      this.selectionsByCartBundle = {
        ...this.selectionsByCartBundle,
        [cartBundleId]: nextBundle
      }
    },
    clearSheetForCartBundle(cartBundleId: string, itemGroupItemId: string) {
      const bundleSelections = this.selectionsByCartBundle[cartBundleId] ?? {}
      if (!(itemGroupItemId in bundleSelections)) return
      const nextBundle = { ...bundleSelections }
      delete nextBundle[itemGroupItemId]
      this.selectionsByCartBundle = {
        ...this.selectionsByCartBundle,
        [cartBundleId]: nextBundle
      }
    },
    /**
     * Opens the editor modal for a single existing cart bundle. Dormant for now
     * — no UI calls this yet; a future edit-after-add affordance (e.g. an "Edit
     * package" control on a cart line item) will. Hydrates from the cart first
     * so the modal reflects the bundle's current selection.
     */
    openForCartBundle(cartBundleId: string) {
      if (this.isSubmitting) return
      this.editCartBundleId = cartBundleId
      this.hydrateFromCart()
      this.isOpen = true
    },
    closeModal() {
      // Race guard mirrors the old `cancel()` — never tear down state while a
      // submit's closure is still using it.
      if (this.isSubmitting) return
      this.isOpen = false
      this.editCartBundleId = null
    },
    /**
     * Writes the given cart bundles' hydrated selections back to the cart. The
     * step passes every SheetSelection bundle; the editor modal passes just its
     * `editCartBundleId`. A configured bundle whose sheets are unchanged is
     * skipped. The rest are overwritten via `cartStore.upsertCartBundle` with the
     * per-sheet unroll (one `quantity: 1` cart item per chosen sheet, kept sheets
     * carrying their poses), the other groups' current picks, and defaults for
     * required groups with none. Rethrows on failure (the caller surfaces it and
     * keeps state for retry); does not navigate or close — the caller does that
     * on success.
     *
     * An upsert with an existing `cartBundleId` replaces that bundle's items,
     * selections and prompt responses (Sunny16 `CartStorefrontService.UpsertCartBundleInternalAsync`),
     * which is why everything the bundle should keep is re-sent.
     */
    async submit(cartBundleIds: string[]) {
      if (this.isSubmitting) return
      if (cartBundleIds.length === 0) return
      if (!cartBundleIds.every((id) => this.isValidForCartBundle(id))) return

      this.isSubmitting = true
      const cartStore = useCartStore()

      try {
        for (const cartBundleId of cartBundleIds) {
          const cartBundle = this.packageBuilderCartBundles.find(
            (cb) => cb.cartBundleId === cartBundleId
          )
          const catalogBundle = this.catalogBundleForCartBundle(cartBundleId)
          const group = this.sheetSelectionGroupForCartBundle(cartBundleId)
          if (!cartBundle || catalogBundle === null || group === null) continue

          // Sunny16 replaces the bundle's items, selections and prompt responses with what an
          // upsert sends, so an unchanged bundle isn't re-sent at all.
          if (
            this.configuredCartBundleIds.includes(cartBundleId) &&
            !this.sheetsChangedForCartBundle(cartBundleId)
          ) {
            continue
          }

          const itemGroups = catalogBundle.bundle?.itemGroups ?? []
          const groupOf = (itemGroupItemId: string) =>
            itemGroups.find((ig) =>
              ig.itemGroupItems?.some((igi) => igi.itemGroupItemId === itemGroupItemId)
            )

          // Each kept sheet takes the prompt responses of a sheet of its kind already in the cart.
          const cartSheetDetails: Record<string, CartItemCustomizationDetailsDto[]> = {}
          for (const cartItem of cartBundle.cartItems ?? []) {
            if (groupOf(cartItem.itemGroupItemId)?.itemGroupId !== group.itemGroupId) continue
            const details = (cartSheetDetails[cartItem.itemGroupItemId] ??= [])
            for (let i = 0; i < (cartItem.quantity || 1); i++) {
              details.push(cartItem.cartItemCustomizationDetails)
            }
          }

          const itemGroupItems = group.itemGroupItems ?? []
          const selections = this.selectionsByCartBundle[cartBundleId] ?? {}
          const sheets: CartItemUpsertStorefrontDto[] = []

          for (const [itemGroupItemId, quantity] of Object.entries(selections)) {
            const igi = itemGroupItems.find((i) => i.itemGroupItemId === itemGroupItemId)
            if (!igi) continue
            // Unroll quantity into N entries of quantity 1 — one bindable pose
            // slot per physical sheet downstream in Select Poses.
            for (let i = 0; i < quantity; i++) {
              const cartItem = new CartItemUpsertStorefrontDto()
              cartItem.itemId = igi.itemId
              cartItem.itemGroupItemId = igi.itemGroupItemId
              cartItem.quantity = 1
              cartItem.cartItemCustomizationDetails = {
                promptResponses: [
                  ...(cartSheetDetails[itemGroupItemId]?.[i]?.promptResponses ?? [])
                ]
              }
              sheets.push(cartItem)
            }
          }

          // The other groups' picks as the cart has them (paper type, options). Included groups
          // are left to Sunny16, which re-adds them and keeps their responses.
          const upsertData = mapCartBundleToCartBundleUpsert(cartBundle)
          const picks = (upsertData.selectedItems ?? []).filter((si) => {
            const itemGroup = groupOf(si.itemGroupItemId)
            return (
              !!itemGroup && itemGroup.itemGroupId !== group.itemGroupId && !itemGroup.isIncluded
            )
          })

          // A required group gets its default only when the cart has no pick in it.
          const pickedGroupIds = new Set(
            picks.map((si) => groupOf(si.itemGroupItemId)?.itemGroupId)
          )
          const defaults = getAutoSelectedRequiredItems(
            catalogBundle.bundle,
            group.itemGroupId
          ).filter((si) => !pickedGroupIds.has(groupOf(si.itemGroupItemId)?.itemGroupId))

          upsertData.quantity = cartBundle.quantity || 1
          upsertData.selectedItems = [...sheets, ...picks, ...defaults]

          await cartStore.upsertCartBundle(upsertData)

          // Mark configured so the step (and the editor modal) hydrate the real
          // selection on return instead of re-presenting an empty panel.
          if (!this.configuredCartBundleIds.includes(cartBundleId)) {
            this.configuredCartBundleIds = [...this.configuredCartBundleIds, cartBundleId]
          }
        }
      } catch (error) {
        appErrorService.reportError(error instanceof Error ? error : new Error(String(error)), {
          context: 'packageBuilderStore.submit'
        })
        throw error
      } finally {
        this.isSubmitting = false
      }
    },
    resetStore(): void {
      // This method is used to reset the store to its initial state manually because of the use of persist
      // Any state property added above should be reset here
      this.isOpen = false
      this.editCartBundleId = null
      this.selectionsByCartBundle = {}
      this.configuredCartBundleIds = []
      this.isSubmitting = false
    }
  },
  persist: {
    // Persist only the "configured" marker so it survives refresh. Everything else
    // (open flag, edit target, working selections, in-flight) is transient.
    omit: ['isOpen', 'editCartBundleId', 'selectionsByCartBundle', 'isSubmitting']
  }
})
